// DatabaseSettingsViewsTab.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { moveOrderedViewById, sortViewsByConfiguredOrder } from 'utilities/ViewOrder';
import { buildViewSelectorOptions } from 'utilities/ViewSelectorOptions';
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';
import { isSystemTypeView } from 'services/views/TypeViewService';

// Components
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';
import DatabaseSettingsViewRow from 'collection/settings/views/DatabaseSettingsViewRow';

/**
 * DatabaseSettingsViewsTab
 *
 * Renders the custom views management tab inside database settings.
 *
 * @param {object[]} views - Custom views available for the active database.
 * @param {string|null} selectedViewId - Currently active view key.
 * @param {function} onSelectView - Selects a view in the database screen.
 * @param {function} onAddView - Opens the create-view editor.
 * @param {function} onEditView - Opens the edit-view editor for a specific view.
 * @param {boolean} [lockSystemViews=false] - When true, system views are read-only rows.
 *
 * @returns {ReactNode} The rendered views management tab.
 */
const DatabaseSettingsViewsTab = React.memo(({
    views = [],
    selectedViewId = null,
    onSelectView,
    onAddView,
    onEditView,
    onReorderView,
    lockSystemViews = false,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [orderedViews, setOrderedViews] = useState([]);
    const [draggingViewId, setDraggingViewId] = useState('');
    const [dragOverViewId, setDragOverViewId] = useState('');

    // ----- MEMOS ----- //

    const configuredViews = useMemo(() => {
        return sortViewsByConfiguredOrder(views);
    }, [views]);

    const viewOptions = useMemo(() => {
        return buildViewSelectorOptions(orderedViews, { fallbackType: 'TABLE' });
    }, [orderedViews]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setOrderedViews(configuredViews);
    }, [configuredViews]);

    // ----- HANDLERS ----- //

    /**
 * Handles drag start.
 *
 * @param {*} view
 * @param {*} event
 */

    const handleDragStart = useCallback((view, event) => {
        if (lockSystemViews && isSystemTypeView(view)) return;
        const nextViewId = String(view?.key || view?.id || '').trim();
        if (!nextViewId) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', nextViewId);
        setDraggingViewId(nextViewId);
    }, [lockSystemViews]);

    /**
 * Handles drag over.
 *
 * @param {*} view
 * @param {*} event
 */
    const handleDragOver = useCallback((view, event) => {
        if (lockSystemViews && isSystemTypeView(view)) return;
        const nextViewId = String(view?.key || view?.id || '').trim();
        if (!draggingViewId || !nextViewId || draggingViewId === nextViewId) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverViewId(nextViewId);
    }, [draggingViewId, lockSystemViews]);

    /**
 * Handles drag end.
 */
    const handleDragEnd = useCallback(() => {
        setDraggingViewId('');
        setDragOverViewId('');
    }, []);

    /**
 * Handles drop.
 *
 * @param {*} view
 * @param {*} event
 */
    const handleDrop = useCallback(async (view, event) => {
        if (lockSystemViews && isSystemTypeView(view)) {
            handleDragEnd();
            return;
        }

        const targetViewId = String(view?.key || view?.id || '').trim();
        if (!draggingViewId || !targetViewId || draggingViewId === targetViewId) {
            handleDragEnd();
            return;
        }

        const draggedView = orderedViews.find((entry) => (
            String(entry?.key || entry?.id || '').trim() === draggingViewId
        ));
        if (lockSystemViews && isSystemTypeView(draggedView)) {
            handleDragEnd();
            return;
        }

        event.preventDefault();

        const previousViews = orderedViews;
        const nextViews = moveOrderedViewById(previousViews, draggingViewId, targetViewId);
        if (!nextViews) {
            handleDragEnd();
            return;
        }

        setOrderedViews(nextViews);
        handleDragEnd();

        try {
            await onReorderView?.(draggingViewId, targetViewId);
        } catch {
            setOrderedViews(previousViews);
        }
    }, [draggingViewId, handleDragEnd, lockSystemViews, onReorderView, orderedViews]);

    // ----- RENDER ----- //

    return (
        <div className="database-modal-views">

            <DatabaseSettingsAddButton
              title="Add View"
              onClick={() => onAddView?.()}
            />

            {viewOptions.length > 0 ? (
                <div className="database-modal-view-list">
                    {viewOptions.map((option) => {
                        const isSelected = option.id === selectedViewId;
                        const isDragging = draggingViewId === option.id;
                        const isDropTarget = dragOverViewId === option.id;
                        const isLocked = lockSystemViews && isSystemTypeView(option.view);

                        return (
                            <DatabaseSettingsViewRow
                              key={option.id}
                              view={option.view}
                              iconKey={getDefaultViewTypeIconKey(option?.type)}
                              isDragging={isDragging}
                              isDropTarget={isDropTarget}
                              isSelected={isSelected}
                              locked={isLocked}
                              badge={isLocked ? 'System' : ''}
                              label={option.label}
                              note={option.note || undefined}
                              onClick={() => {
                                    if (isLocked) return;
                                    onSelectView?.(option.id);
                              }}
                              onDragEnd={handleDragEnd}
                              onDragOver={(event) => handleDragOver(option.view || null, event)}
                              onDragStart={(event) => handleDragStart(option.view || null, event)}
                              onDrop={(event) => handleDrop(option.view || null, event)}
                              onSettingsClick={() => {
                                    if (isLocked) return;
                                    onEditView?.(option.view || null);
                              }}
                            />
                        );
                    })}
                </div>
            ) : (
                <div
                  className="database-modal-empty-state"
                  style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    No custom views yet.
                </div>
            )}

        </div>
    );
});

// Dev Tools
DatabaseSettingsViewsTab.displayName = 'DatabaseSettingsViewsTab';

export default DatabaseSettingsViewsTab;
